import { ApiError } from "@/lib/apiClient";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Collection,
  FetchCollectionsOptions,
  SearchCollectionsOptions,
  CreateCollectionRequest,
  UpdateCollectionRequest,
} from "@/services/collectionService";
import { pagination, FormState } from "@/types";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

interface CollectionsState {
  // List state
  items: Collection[];
  loading: boolean;
  error: ApiError | null;
  pagination: pagination;

  // Single collection state
  currentCollection: Collection | null;
  collectionLoading: boolean;
  collectionError: ApiError | null;

  // Search state
  searchResults: Collection[];
  searchLoading: boolean;
  searchError: ApiError | null;
  searchPagination: pagination;

  // Form state
  formState: FormState;

  // Product management state
  productManagementLoading: boolean;
  productManagementError: ApiError | null;
}

const initialState: CollectionsState = {
  items: [],
  loading: false,
  error: null,
  pagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  currentCollection: null,
  collectionLoading: false,
  collectionError: null,
  searchResults: [],
  searchLoading: false,
  searchError: null,
  searchPagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  formState: {
    loading: false,
    success: false,
    error: null,
  },
  productManagementLoading: false,
  productManagementError: null,
};

const collectionsSlice = createSlice({
  name: "collections",
  initialState,
  reducers: {
    // Fetch Collections
    fetchCollectionsRequest: (
      state,
      _action: PayloadAction<FetchCollectionsOptions>,
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchCollectionsSuccess: (
      state,
      action: PayloadAction<{
        list: Collection[];
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      }>,
    ) => {
      state.items = action.payload.list;
      state.pagination.page = action.payload.page;
      state.pagination.total = action.payload.total;
      state.pagination.pageSize = action.payload.pageSize;
      state.pagination.totalPages = action.payload.totalPages;
      state.loading = false;
    },
    fetchCollectionsFailure: (state, action: PayloadAction<ApiError>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Single Collection Operations
    fetchCollectionRequest: (
      state,
      _action: PayloadAction<{ collectionId: string }>,
    ) => {
      state.collectionLoading = true;
      state.collectionError = null;
    },
    fetchCollectionSuccess: (
      state,
      action: PayloadAction<{ collection: Collection }>,
    ) => {
      state.currentCollection = action.payload.collection;
      state.collectionLoading = false;
      state.collectionError = null;
    },
    fetchCollectionFailure: (state, action: PayloadAction<ApiError>) => {
      state.collectionLoading = false;
      state.collectionError = action.payload;
    },

    // Create Collection
    createCollectionRequest: (
      state,
      _action: PayloadAction<CreateCollectionRequest>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    createCollectionSuccess: (
      state,
      action: PayloadAction<{
        collection: Collection;
        shouldNavigate?: boolean;
      }>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items.unshift(action.payload.collection);
      state.pagination.total += 1;
    },
    createCollectionFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
      state.formState.success = false;
    },

    // Update Collection
    updateCollectionRequest: (
      state,
      _action: PayloadAction<{
        collectionId: string;
        collection: UpdateCollectionRequest;
      }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    updateCollectionSuccess: (
      state,
      action: PayloadAction<{
        collection: Collection;
        shouldNavigate?: boolean;
      }>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      const index = state.items.findIndex(
        (item) => item.id === action.payload.collection.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload.collection;
      }
      if (state.currentCollection?.id === action.payload.collection.id) {
        state.currentCollection = action.payload.collection;
      }
    },
    updateCollectionFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
      state.formState.success = false;
    },

    // Delete Collection
    deleteCollectionRequest: (
      state,
      _action: PayloadAction<{ collectionId: string }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
    },
    deleteCollectionSuccess: (
      state,
      action: PayloadAction<{ collectionId: string }>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items = state.items.filter(
        (item) => item.id !== action.payload.collectionId,
      );
      state.pagination.total = Math.max(0, state.pagination.total - 1);
      if (state.currentCollection?.id === action.payload.collectionId) {
        state.currentCollection = null;
      }
    },
    deleteCollectionFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    // Search Collections
    searchCollectionsRequest: (
      state,
      _action: PayloadAction<SearchCollectionsOptions>,
    ) => {
      state.searchLoading = true;
      state.searchError = null;
    },
    searchCollectionsSuccess: (
      state,
      action: PayloadAction<{
        list: Collection[];
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      }>,
    ) => {
      state.searchResults = action.payload.list;
      state.searchPagination.page = action.payload.page;
      state.searchPagination.total = action.payload.total;
      state.searchPagination.pageSize = action.payload.pageSize;
      state.searchPagination.totalPages = action.payload.totalPages;
      state.searchLoading = false;
    },
    searchCollectionsFailure: (state, action: PayloadAction<ApiError>) => {
      state.searchError = action.payload;
      state.searchLoading = false;
    },

    // Product Management
    addProductsToCollectionRequest: (
      state,
      _action: PayloadAction<{ collectionId: string; productIds: string[] }>,
    ) => {
      state.productManagementLoading = true;
      state.productManagementError = null;
    },
    addProductsToCollectionSuccess: (
      state,
      action: PayloadAction<{ collectionId: string; addedCount: number }>,
    ) => {
      state.productManagementLoading = false;
      // Update products count if available
      if (state.currentCollection) {
        state.currentCollection.products_count =
          (state.currentCollection.products_count || 0) +
          action.payload.addedCount;
      }
    },
    addProductsToCollectionFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.productManagementLoading = false;
      state.productManagementError = action.payload;
    },

    removeProductsFromCollectionRequest: (
      state,
      _action: PayloadAction<{ collectionId: string; productIds: string[] }>,
    ) => {
      state.productManagementLoading = true;
      state.productManagementError = null;
    },
    removeProductsFromCollectionSuccess: (
      state,
      action: PayloadAction<{ collectionId: string; removedCount: number }>,
    ) => {
      state.productManagementLoading = false;
      // Update products count if available
      if (state.currentCollection) {
        state.currentCollection.products_count = Math.max(
          0,
          (state.currentCollection.products_count || 0) -
            action.payload.removedCount,
        );
      }
    },
    removeProductsFromCollectionFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.productManagementLoading = false;
      state.productManagementError = action.payload;
    },

    // Form State Management
    resetFormState: (state) => {
      state.formState.loading = false;
      state.formState.error = null;
      state.formState.success = false;
    },

    // Clear states
    clearCurrentCollection: (state) => {
      state.currentCollection = null;
      state.collectionError = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  // Fetch collections
  fetchCollectionsRequest,
  fetchCollectionsSuccess,
  fetchCollectionsFailure,

  // Single collection
  fetchCollectionRequest,
  fetchCollectionSuccess,
  fetchCollectionFailure,

  // CRUD operations
  createCollectionRequest,
  createCollectionSuccess,
  createCollectionFailure,
  updateCollectionRequest,
  updateCollectionSuccess,
  updateCollectionFailure,
  deleteCollectionRequest,
  deleteCollectionSuccess,
  deleteCollectionFailure,

  // Search
  searchCollectionsRequest,
  searchCollectionsSuccess,
  searchCollectionsFailure,

  // Product management
  addProductsToCollectionRequest,
  addProductsToCollectionSuccess,
  addProductsToCollectionFailure,
  removeProductsFromCollectionRequest,
  removeProductsFromCollectionSuccess,
  removeProductsFromCollectionFailure,

  // Utilities
  resetFormState,
  clearCurrentCollection,
  clearSearchResults,
} = collectionsSlice.actions;

export default collectionsSlice.reducer;
